import { DataObject } from './dataobject';
import { MissionControlDatabase } from './db';
import { IDBPDatabase, openDB } from 'idb';
import { exportDB as dexieExportDB } from 'dexie-export-import';
import BaseTable, { TableIntegrityResult } from './basetable';
import logger from '../logger';
import * as Sentry from '@sentry/react';
import { wait } from '../utils';
import { DBPeriodicSync, PeriodicDbSyncIntervalSeconds } from './local_storage';
import { LocalStorageGenerator } from '../utils';
import { alertError } from '../alertDispatcher';
import Attachment from './AttachmentClass';
import { purgeStore, storeDispatch } from '../redux/store';
import { addMissingBoxes, deleteAllBoxes } from '../redux/features/boxes/boxesSlice';
import SampleBox from './SampleBoxClass';
import recoveryServerDbManager, { DB_SAVE_REASONS } from '../services/RecoveryServerDbManager';
import JSZip from 'jszip';
import s3 from '../aws';
import { useRecoveryServer } from '../recoveryServerUtils';
import { TaskName } from './TaskClass';
import {
  AWS_TESTING_MULTIPUPROSE_BUCKET,
  PERIODIC_DB_SYNC_NON_SAMPLING_SECONDS,
  PERIODIC_DB_SYNC_SAMPLING_SECONDS,
} from '../constants';
import { fileToBase64 } from '../utilities/file-utils';
import PeriodicSyncWorker from './periodicSync.worker?worker&url';
import { getCurrentUser } from '../dataModelHelpers';

const SYNC_DELAY_MS = 1000;

let syncDelayTimeout: NodeJS.Timeout | undefined = undefined;
let syncInProgress = false;

export const DataLayerStorage = LocalStorageGenerator<'idb' | 'dexie'>('dataLayerStorage', 'dexie');

export const tableList: Record<string, BaseTable<DataObject>> = {};

let totalSyncTime = 0;
let totalSyncCount = 0;

const syncWorker = new Worker(PeriodicSyncWorker, {
  type: 'module',
});

syncWorker.onmessage = async (e) => {
  const { ok, error, inMemoryData } = e.data;

  if (!ok) {
    console.error('DB sync worker error:', error);

    logger.log('PERIODIC_DB_SYNC - syncWorker.onmessage', `Error in periodic sync: ${JSON.stringify(error)}`);

    return;
  }

  if (!Array.isArray(inMemoryData)) {
    const errorMessage = 'DB sync worker failed - no data returned';
    console.error(errorMessage);

    logger.log('PERIODIC_DB_SYNC - syncWorker.onmessage', errorMessage);

    return;
  }

  try {
    const db = getDatabase();
    const result = await db.MemoryBackup.put({
      timestamp: Date.now(),
      data: inMemoryData[0],
      name: 'auto',
      checksum: '',
    });

    // console.log('Periodic DB Sync, id:', result);

    // Make sure our last memory backup is saved successfully before deleting old backups
    if (result) {
      deleteOldMemoryBackups(db);
    }
  } catch (e) {
    console.error('Error saving periodic sync data from worker', e);
    logger.log('PERIODIC_DB_SYNC', `Error in periodic sync: ${JSON.stringify(e)}`);
  }
};

async function runPeriodicSyncInWorker() {
  const db = prepareTablesForSerializing();

  syncWorker.postMessage({ db });
}

function deleteIndexDbDatabase(dbName: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.deleteDatabase(dbName);
    request.onsuccess = () => resolve();
    request.onerror = (e) => reject(request.error);

    request.onblocked = () => {
      console.warn('deleteIndexDbDatabase - delete blocked, possibly due to open connections');
    };
  });
}

export async function clearIndexDB(dbName: string) {
  try {
    await Promise.race([
      deleteIndexDbDatabase(dbName).then(() => {
        console.log('Deleted IndexDb', dbName);
      }),
      new Promise((resolve) => setTimeout(resolve, 10000)),
    ]);
  } catch (e) {
    console.error('Failed to reset database', e);
    alertError('Failed to reset database');
  }
}

async function clearAllData() {
  console.log('CLEARING ALL DATA: IndexDB + Local Storage + Redux Store');

  // wipe local storage
  localStorage.clear();

  // wipe IndexDB
  await clearIndexDB(DB_NAME);

  // wipe redux store
  await purgeStore();
}

export async function validateAllTableDataIntegrity() {
  // const results = await Promise.all(Object.values(tableList).map(table => table.dataIntegriyCheck()));
  const results: TableIntegrityResult[] = [];

  for (const tableName of Object.keys(tableList)) {
    try {
      results.push(await tableList[tableName].dataIntegriyCheck());
    } catch (e) {
      // TODO capture sentry error
      console.error('Error in data integrity check', e);
    }
  }

  const allRecordsGood = results.every((result) => result.results.valid);

  if (!allRecordsGood) {
    return false;
  }

  return true;
}

export function getMemoryTables() {
  return Object.values(tableList);
}

// Retrieve a data table.
export function getMemoryTable(tableName: string) {
  return tableList[tableName];
}

export async function deleteAllRecords({ exceptTables }: { exceptTables: string[] }) {
  for (const tableName of Object.keys(tableList)) {
    if (exceptTables.some((_tableName) => _tableName === tableName)) {
      continue;
    }

    const table = getMemoryTable(tableName);
    table.deleteAll();

    if (tableName === 'SampleBox') {
      storeDispatch(deleteAllBoxes());
    }
  }
}

// Register a new data table.

let periodicTimer: NodeJS.Timer | undefined;
const STARTUP_DELAY = 5;

// Load all tables from IndexedDB into memory.
// If no DB backend exists, do nothing.
export async function loadAllTables() {
  const db = getDatabase();

  if (db) {
    if (DataLayerStorage.get() === 'dexie') {
      // NEW DB CODE
      await Promise.all(Object.values(tableList).map((table) => table.restoreData()));

      // Put existing sample boxes to new boxes redux store - needed
      // for the fist time the app is loaded after introducing the new store.
      storeDispatch(addMissingBoxes(SampleBox.query()));
    } else {
      // OLD DB CODE
      // const tx = db.transaction(Object.keys(tableList), 'readwrite');
      // await Promise.all(Object.values(tableList).map((table) => table.initializeData(tx)));
    }
  } else {
    // if there is no DB backend, simply reset all tables changed marker
    Object.values(tableList).forEach((table) => table.resetSync());
  }

  if (DBPeriodicSync.get()) {
    // start the sync process after a few seconds
    setTimeout(resumePeriodicSync, STARTUP_DELAY * 1000);
  }
}

export const pausePeriodicSync = () => {
  if (periodicTimer) {
    // @ts-ignore
    clearInterval(periodicTimer);
    periodicTimer = undefined;
  }
};

const resumePeriodicSync = () => {
  if (!periodicTimer) {
    const { savingTimeFrameMs } = getSavingAndDeleteTimeFrameMs();

    periodicTimer = setInterval(periodicDBSync, savingTimeFrameMs);
  }
};

export function restartPeriodicDbSync(task: TaskName) {
  let interval: number = PERIODIC_DB_SYNC_NON_SAMPLING_SECONDS;

  switch (task) {
    case TaskName.SAMPLING:
      interval = PERIODIC_DB_SYNC_SAMPLING_SECONDS;

      break;
    default:
      interval = PERIODIC_DB_SYNC_NON_SAMPLING_SECONDS;
  }

  PeriodicDbSyncIntervalSeconds.set(interval);

  pausePeriodicSync();

  resumePeriodicSync();
}

export async function importDB(data: string) {
  await clearAllData();

  const db = getDatabase();

  await db.MemoryBackup.put({
    timestamp: Date.now(),
    data: new Blob([data]),
    name: 'auto',
    checksum: '',
  });

  await loadAllTables();

  return true;
}

function prepareTablesForSerializing() {
  const db = {};

  Object.entries(tableList).forEach(
    ([tableName, table]: [string, BaseTable<any>]) => (db[tableName] = table.query(() => true)),
  );

  return db;
}

async function prepareDbForSerializing() {
  const db = prepareTablesForSerializing();

  await Promise.all(
    db['Attachment'].map(async (attachment: Attachment) => {
      return new Promise(async (res) => {
        attachment['contentB64'] = await fileToBase64(attachment.content as Blob);

        res(attachment);
      });
    }),
  );

  return db;
}

// export all data tables as JSON blob
export async function exportDB(fromIndexedDB = false) {
  if (fromIndexedDB) {
    try {
      pausePeriodicSync();

      const { savingTimeFrameMs } = getSavingAndDeleteTimeFrameMs();
      // wait periodic time to ensure last write is finished
      await wait(savingTimeFrameMs);

      if (DBPeriodicSync.get()) {
        return [await dexieExportDB(getDatabase()), 'alldata_idb.json', 'application/json'] as const;
      }
    } catch (e) {
      console.error('Error exporting IndexedDB data', e);
    } finally {
      resumePeriodicSync();
    }

    return;
  }

  const db = await prepareDbForSerializing();

  const stringData = JSON.stringify(db);

  // TODO transform blob entries to base64
  return [new Blob([stringData]), 'alldata.json', 'application/json'] as const;
}

// allow the app to request a sync
export async function requestDBSync() {
  if (localStorage.getItem('liveMode') === 'true') {
    return;
  }

  // If no sync is in progress, initiate sync immediately. Otherwise delay the
  // sync. If a sync is already delayed, reset the delay.
  if (syncInProgress) {
    clearTimeout(syncDelayTimeout);
    // Attempt a random delay to avoid more collissions for syncing
    syncDelayTimeout = setTimeout(requestDBSync, SYNC_DELAY_MS); //Math.random() * SYNC_DELAY_MS + MIN_SYNC_DELAY_MS);
    // do a console.count with the time in seconds so we get a count every second
    // console.count(`Sync Delayed ${Math.round(Date.now() / 1000)}`);
  } else {
    await syncAllTables().catch((e) => {
      // alertError('Error in background DB sync');
      Sentry.captureException(e);
      const forceSyncData = Object.keys(tableList)
        .map((tableName) => `${tableName}: ${getMemoryTable(tableName).requiresForceSync}`)
        .join(', ');
      logger.log('SYNC_ALL_TABLES', `Error in background DB sync: ${e}, force sync: ${forceSyncData}`);
      // console.error('Error in background DB sync: ' + e);
    });
    syncDelayTimeout = undefined;
  }
}

// execute a sync
async function syncAllTables() {
  console.count('Sync All Tables');
  syncInProgress = true;

  try {
    const changedTables = Object.keys(tableList).filter((tableName) => getMemoryTable(tableName).needsSync());

    if (changedTables.length) {
      console.debug(`Syncing ${changedTables.length} tables (${changedTables.join(', ')})`);
      // create IndexedDB transaction
      const t0 = performance.now();

      // sync tables
      const changedTablesEntries = Object.entries(tableList).filter(([key, table]) => changedTables.includes(key));

      for (const [, table] of changedTablesEntries) {
        await table.syncData();
      }

      // commit transaction
      const t1 = performance.now();

      const duration = t1 - t0;
      totalSyncTime += duration;
      totalSyncCount += 1;
      const averageDuration = totalSyncTime / totalSyncCount;
      console.debug(`${changedTables.length} tables synced in ${duration}ms, average ${averageDuration}`, t1 - t0);

      if (duration > averageDuration * 1.1) {
        // log long sync
        //await logger.log('SYNC_ALL_TABLES', `longSync ${{ duration, averageDuration, changedTables }}`);
      }
    }
  } finally {
    syncInProgress = false;
  }
}

// Initialize the DB
export const DB_NAME = 'missioncontrol';

let dbInstOld: IDBPDatabase<any>;

export async function initOldDatabase() {
  // Initialize the DB
  const DB_VERSION = 9;

  // initialize database
  if (dbInstOld) {
    return dbInstOld;
  }

  dbInstOld = await openDB(DB_NAME, DB_VERSION, {
    upgrade(upgradeDB: IDBPDatabase<unknown>, oldVersion, newVersion, transaction) {
      Object.keys(tableList).forEach((tableName) => {
        if (![...upgradeDB.objectStoreNames].includes(tableName)) {
          upgradeDB.createObjectStore(tableName, { keyPath: '_instance_id' });
        }
      });
    },
  });

  return dbInstOld;
}

const getSavingAndDeleteTimeFrameMs = (): { savingTimeFrameMs: number; deleteTimeFrameMs: number } => {
  const savingTimeFrameSeconds = PeriodicDbSyncIntervalSeconds.get();
  const savingTimeFrameMs = savingTimeFrameSeconds * 1000;

  const itemsToKeep = 50;
  const deleteTimeFrameMs = itemsToKeep * savingTimeFrameMs;

  return { savingTimeFrameMs, deleteTimeFrameMs };
};

let dbInstNew: MissionControlDatabase | undefined = undefined;

export function initNewDatabase() {
  // initialize database
  if (!dbInstNew) {
    dbInstNew = new MissionControlDatabase();
  }

  return dbInstNew;
}

let periodicDBSyncLastCalled = 0;

async function periodicDBSync() {
  await runPeriodicSyncInWorker();

  // Save db to recovery server every minute
  const now = Date.now();

  if (now - periodicDBSyncLastCalled >= 60_000) {
    periodicDBSyncLastCalled = now;
    const loggedInUser = getCurrentUser();

    // Only save db to recovery server if user is logged in
    if (loggedInUser) {
      saveDbToRecoveryServer(DB_SAVE_REASONS.AUTO).catch((err) => {
        console.error('Failed to save DB to recovery server in periodic sync', err);
      });
    }
  }
}

export async function getDbInfoFromRecoveryServer(filename: string) {
  const fetchedDb = await recoveryServerDbManager.fetchDbWithFilename(filename.replace('.zip', ''));

  if (!fetchedDb) {
    return false;
  }

  const { content } = fetchedDb;

  return content;
}

export async function loadDbFromRecoveryServerWithFilename(filename: string) {
  const fetchedDb = await recoveryServerDbManager.fetchDbWithFilename(filename.replace('.zip', ''));

  if (!fetchedDb) {
    return false;
  }

  const { content } = fetchedDb;

  const result = await importDB(content);

  if (result) {
    window.location.reload();

    return true;
  }

  return false;
}

export async function loadBenchmarkDbFromS3(filename: string) {
  console.log('loadBenchmarkDbFromS3 - filename', filename);
  const response = await s3.download(AWS_TESTING_MULTIPUPROSE_BUCKET, `mc-benchmark/${filename}`);

  if (!response) {
    console.warn('loadBenchmarkDbFromS3 failed - no response');

    return false;
  }

  const zipFile = new File([response as any], 'temp.zip', { type: 'application/zip' });

  const jsZip = new JSZip();
  const zip = await jsZip.loadAsync(zipFile);

  const jsonFile = Object.keys(zip.files).find((fileKey) => fileKey.endsWith('.json'));

  if (!jsonFile) {
    return undefined;
  }

  const content = await zip.files[jsonFile].async('text');

  return await importDB(content);
}

export async function saveDbToRecoveryServer(saveReason: DB_SAVE_REASONS): Promise<boolean> {
  if (!useRecoveryServer()) {
    return false;
  }

  await wait(1000); // wait a little to let any ongoing changes finish

  logger.log('saveDbToRecoveryServer - start', { saveReason });

  let inMemoryData: readonly [Blob, string, string] | undefined;

  try {
    inMemoryData = await exportDB(false);
  } catch (error) {
    logger.log('saveDbToRecoveryServer - error', { error, saveReason });

    return false;
  }

  if (!inMemoryData) {
    logger.log('saveDbToRecoveryServer - no inMemoryData found', { saveReason });

    return false;
  }

  const success = await recoveryServerDbManager.saveDb(inMemoryData[0], saveReason);

  if (!success) {
    logger.log('saveDbToRecoveryServer - saving failed', { saveReason });

    return false;
  }

  return true;
}

async function deleteOldMemoryBackups(db: MissionControlDatabase) {
  // delete backups older than the calculated timeframe (number of items * seconds / item save = seconds of valid data)
  //                           seconds per
  // number of items to keep * -----------  = seconds of valid backups
  //                            item save

  const { deleteTimeFrameMs } = getSavingAndDeleteTimeFrameMs();
  const oldest = Date.now() - deleteTimeFrameMs;
  await db.MemoryBackup.where('timestamp').below(oldest).delete();
}

// let dbInst:
export function getDatabase() {
  // NEW DB CODE
  return initNewDatabase();

  // OLD DB CODE
  // return initOldDatabase();
}

// Integrity error definition
export class IntegrityError extends Error {
  constructor(message: string | undefined) {
    super(message);
    this.name = 'IntegrityError';
  }
}
